import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  OnDestroy,
  signal,
} from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { IonButton } from '@ionic/angular';
import { ToastController } from '@ionic/angular';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { marker } from '@jsverse/transloco-keys-manager/marker';
import { finalize, of } from 'rxjs';

import { AccountDetails, MinistrySurveyAnswers, RosterParticipant } from '../../../models';
import {
  AccountDetailsFormGroup,
  CategoryGroup,
  MyInfoField,
  MyInfoFormKey,
  SpecialtySelection,
} from '../../../models/forms-models';
import { MyInfoFieldsComponent } from '../../forms/my-info-fields/my-info-fields.component';
import { MinistrySurveyCardComponent } from '../../forms/ministry-survey-card/ministry-survey-card.component';
import { InlineAlertComponent } from '../../ui/inline-alert/inline-alert.component';
import { GroupManagementService, ParticipantInfoPatch } from '@services/group-management.service';
import { MyInfoService } from '@services/my-info.service';
import { PanelHostService } from '@services/panel-host.service';
import { rosterParticipantFromPanelContext } from '@services/panel-store';
import { FormsService } from '@services/forms.service';
import { TripService } from '@services/trip.service';
import {
  buildFormSnapshot,
  buildFormSnapshotFromValues,
  computeDirtyFormPatch,
  specialtiesEqual,
} from '@services/forms/my-info-autosave-patch';
import { MinistrySurveyService } from '@services/ministry-survey/ministry-survey.service';
import {
  buildMinistrySurveyForm,
  computeDirtyMinistrySurveyPatch,
  MinistrySurveyFormGroup,
  ministrySurveyAnswersFromValues,
  MinistrySurveyPatch,
} from '@services/ministry-survey/ministry-survey-fields';
import { ContentContainerComponent } from '../../ui/content-container/content-container.component';
import { PanelBodyComponent } from '../../ui/panel-body/panel-body.component';

function rosterParticipantToAccountDetails(participant: RosterParticipant): AccountDetails {
  return {
    ...participant,
    displayName: `${participant.firstname ?? ''} ${participant.lastname ?? ''}`.trim(),
    hasProfilePhoto: false,
    profilePhotoUrl: null,
  };
}

/** The leader's Ministry Survey edit for one `tripId:userId` pair. */
interface MinistrySurveyEditState {
  key: string;
  form: MinistrySurveyFormGroup;
  snapshot: MinistrySurveyAnswers;
}

@Component({
  selector: 'app-edit-info',
  imports: [
    TranslocoDirective,
    MyInfoFieldsComponent,
    MinistrySurveyCardComponent,
    InlineAlertComponent,
    IonButton,
    ContentContainerComponent,
    PanelBodyComponent,
  ],
  templateUrl: './edit-info.component.html',
  styleUrls: ['./edit-info.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EditInfoComponent implements OnDestroy {
  private readonly panelHost = inject(PanelHostService);
  private readonly tripService = inject(TripService);
  private readonly formsService = inject(FormsService);
  private readonly myInfoService = inject(MyInfoService);
  private readonly groupManagementService = inject(GroupManagementService);
  private readonly ministrySurveyService = inject(MinistrySurveyService);
  private readonly transloco = inject(TranslocoService);
  private readonly toastController = inject(ToastController);

  readonly submitting = signal(false);

  readonly participant = computed((): RosterParticipant | null =>
    rosterParticipantFromPanelContext(this.panelHost.rosterPanelContext()),
  );

  readonly tripId = computed(() => this.tripService.currentTrip.value()?.id ?? null);

  readonly fieldsResource = rxResource({
    params: () => ({
      tripId: this.tripId(),
      userId: this.participant()?.id ?? null,
      panel: this.panelHost.activePanel(),
    }),
    stream: ({ params }) => {
      if (params.panel !== 'edit-info' || params.tripId == null || params.userId == null) {
        return of([] as MyInfoField[]);
      }
      return this.formsService.getFieldsForTrip(String(params.tripId), params.userId);
    },
  });

  /**
   * The trip offers the Ministry Survey and the target is a roster participant
   * (the panel only opens on `roster` entries). The server re-checks both.
   */
  readonly ministrySurveyOffered = computed(
    (): boolean =>
      this.tripService.currentTrip.value()?.ministrySurveyOffered === true && this.participant() !== null,
  );

  /** Trip-scoped answers, read through the same authorized resource as My Info. */
  readonly ministrySurveyResource = rxResource({
    params: () => ({
      tripId: this.tripId(),
      userId: this.participant()?.id ?? null,
      offered: this.ministrySurveyOffered(),
      panel: this.panelHost.activePanel(),
    }),
    stream: ({ params }) => {
      if (
        params.panel !== 'edit-info' ||
        !params.offered ||
        params.tripId == null ||
        params.userId == null
      ) {
        return of(null);
      }
      return this.ministrySurveyService.getAnswers(params.tripId, params.userId);
    },
  });

  /** A separate form and snapshot: the answers belong to the trip, not to account details. */
  readonly ministrySurveyState = signal<MinistrySurveyEditState | null>(null);

  readonly myInfoState = signal<{
    form: AccountDetailsFormGroup<MyInfoFormKey>;
    fieldsByCategory: CategoryGroup[];
    formKeys: MyInfoFormKey[];
  } | null>(null);

  readonly specialtiesSelection = signal<SpecialtySelection[]>([]);
  private readonly initialSnapshot = signal<Record<
    MyInfoFormKey,
    string | number | boolean | null
  > | null>(null);
  private readonly initialSpecialties = signal<SpecialtySelection[]>([]);

  readonly specialtyCatalog = this.myInfoService.specialtyCatalog;
  readonly specialtyCatalogLoadFailed = this.myInfoService.specialtyCatalogLoadFailed;

  readonly loading = computed(() => {
    const participant = this.participant();
    if (!participant) {
      return false;
    }
    if (this.fieldsResource.isLoading() || this.ministrySurveyResource.isLoading()) {
      return true;
    }
    return this.fieldsResource.value() !== undefined && !this.myInfoState();
  });

  readonly ministrySurveyLoadFailed = computed(
    (): boolean => this.ministrySurveyOffered() && this.ministrySurveyResource.status() === 'error',
  );

  private formInitKey: string | null = null;

  constructor() {
    effect(() => {
      const participant = this.participant();
      const fields = this.fieldsResource.value();
      const panel = this.panelHost.activePanel();

      if (panel !== 'edit-info') {
        this.resetFormState();
        return;
      }

      if (!participant || fields === undefined) {
        return;
      }

      const initKey = `${participant.id}:${fields.map((field) => field.id).join(',')}`;
      if (initKey === this.formInitKey) {
        return;
      }

      this.formInitKey = initKey;
      const account = rosterParticipantToAccountDetails(participant);
      const state = this.formsService.buildMyInfoFormState(fields, account, {
        enforceRequired: false,
      });
      const formKeys = fields
        .filter((field): field is MyInfoField & { id: MyInfoFormKey } => field.id !== 'specialties')
        .map((field) => field.id);

      this.myInfoState.set({ ...state, formKeys });
      const specialties =
        participant.specialties?.map((item) => ({
          specialtyId: item.specialtyId,
          otherValue: item.otherValue ?? '',
        })) ?? [];
      this.specialtiesSelection.set(specialties);
      this.initialSnapshot.set(buildFormSnapshot(account, formKeys));
      this.initialSpecialties.set([...specialties]);
    });

    effect(() => {
      const answers = this.ministrySurveyResource.hasValue() ? this.ministrySurveyResource.value() : null;
      const tripId = this.tripId();
      const participant = this.participant();
      if (this.panelHost.activePanel() !== 'edit-info' || !answers || tripId == null || !participant) {
        return;
      }

      const key = `${tripId}:${participant.id}`;
      if (this.ministrySurveyState()?.key === key) {
        return;
      }
      this.ministrySurveyState.set({ key, form: buildMinistrySurveyForm(answers), snapshot: answers });
    });

    effect(() => {
      if (this.panelHost.activePanel() !== 'edit-info' || !this.myInfoState()) {
        return;
      }
      this.panelHost.registerDirtyPredicate(() => this.isDirty());
    });
  }

  ngOnDestroy(): void {
    this.panelHost.clearDirtyPredicate();
  }

  onSpecialtiesSelectionChange(selection: SpecialtySelection[]): void {
    this.specialtiesSelection.set(selection);
  }

  cancel(): void {
    void this.panelHost.dismiss();
  }

  save(): void {
    const participant = this.participant();
    const tripId = this.tripId();
    const state = this.myInfoState();
    const snapshot = this.initialSnapshot();
    if (!participant || tripId == null || !state || !snapshot) {
      return;
    }

    const formPatch = computeDirtyFormPatch(
      state.form.getRawValue(),
      snapshot,
      state.formKeys,
    );
    const specialtiesChanged = !specialtiesEqual(
      this.specialtiesSelection(),
      this.initialSpecialties(),
    );
    const surveyPatch = this.ministrySurveyPatch();
    const surveyChanged = Object.keys(surveyPatch).length > 0;

    if (Object.keys(formPatch).length === 0 && !specialtiesChanged && !surveyChanged) {
      void this.panelHost.dismiss();
      return;
    }

    const payload = {
      ...formPatch,
      ...(specialtiesChanged ? { specialties: this.specialtiesSelection() } : {}),
      ...(surveyChanged ? { ministrySurvey: surveyPatch } : {}),
    } as ParticipantInfoPatch;

    this.submitting.set(true);
    this.groupManagementService
      .patchParticipantInfo(tripId, participant.id, payload)
      .pipe(finalize(() => this.submitting.set(false)))
      .subscribe({
        next: async () => {
          const toast = await this.toastController.create({
            message: this.transloco.translate(
              marker('group-management.panels.edit-info.success'),
              { name: participant.firstname },
            ),
            duration: 4000,
            color: 'success',
          });
          await toast.present();
          this.syncDirtyBaseline();
          void this.panelHost.dismiss();
        },
        error: async () => {
          const toast = await this.toastController.create({
            message: this.transloco.translate(marker('group-management.panels.edit-info.error'), {
              name: participant.firstname,
            }),
            duration: 4000,
            color: 'danger',
          });
          await toast.present();
        },
      });
  }

  private isDirty(): boolean {
    const state = this.myInfoState();
    const snapshot = this.initialSnapshot();
    if (!state || !snapshot) {
      return false;
    }

    const formPatch = computeDirtyFormPatch(
      state.form.getRawValue(),
      snapshot,
      state.formKeys,
    );
    if (Object.keys(formPatch).length > 0) {
      return true;
    }

    if (Object.keys(this.ministrySurveyPatch()).length > 0) {
      return true;
    }

    return !specialtiesEqual(this.specialtiesSelection(), this.initialSpecialties());
  }

  /** Survey answers that differ from the loaded snapshot; empty when the survey is not shown. */
  private ministrySurveyPatch(): MinistrySurveyPatch {
    const survey = this.ministrySurveyState();
    if (!survey) {
      return {};
    }
    return computeDirtyMinistrySurveyPatch(survey.form.getRawValue(), survey.snapshot);
  }

  /** After a successful save, align the dirty baseline with the persisted form state. */
  private syncDirtyBaseline(): void {
    const state = this.myInfoState();
    if (!state) {
      return;
    }

    this.initialSnapshot.set(
      buildFormSnapshotFromValues(state.form.getRawValue(), state.formKeys),
    );
    this.initialSpecialties.set(
      this.specialtiesSelection().map((item) => ({
        specialtyId: item.specialtyId,
        otherValue: item.otherValue ?? '',
      })),
    );

    const survey = this.ministrySurveyState();
    if (survey) {
      this.ministrySurveyState.set({
        ...survey,
        snapshot: ministrySurveyAnswersFromValues(survey.form.getRawValue()),
      });
    }
  }

  private resetFormState(): void {
    this.formInitKey = null;
    this.myInfoState.set(null);
    this.specialtiesSelection.set([]);
    this.initialSnapshot.set(null);
    this.initialSpecialties.set([]);
    this.ministrySurveyState.set(null);
  }
}
